import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "~db/db";
import { semanaVigente, todasAsDatasDoMes } from "~services/GlobalServices";

const useVisitas = ({ skip, take, filters, vendedorSelecionado }) => {
  const [visitas, setVisitas] = useState([]);
  const [days, setDays] = useState([]);
  const [uniqueLocais, setUniqueLocais] = useState([]);
  const { parameter: idCliente } = useParams();

  useLiveQuery(async () => {
    if (!days.length) return;

    let recebedorSet = new Set();
    let emissorSet = new Set();
    let prospectSet = new Set();
    if (filters.tipoCliente) {
      const recebedorIds = await db.cliente.filter(c => (c.tipo === 3 || c.tipo === 4) && c.clienteProspect === 0).primaryKeys();
      recebedorSet = new Set(recebedorIds);
      const emissorIds = await db.cliente.filter(c => (c.tipo === 1 || c.tipo === 2) && c.clienteProspect === 0).primaryKeys();
      emissorSet = new Set(emissorIds);
      const prospectIds = await db.cliente.filter(c => c.clienteProspect === 1).primaryKeys();
      prospectSet = new Set(prospectIds);
    }

    let query = db.visitas
      .where("dataLabel")
      .anyOf(days)
      .and((a) => {
        const searchMatch = filters.search
          ? [a.codigoCliente ?? "", a.nomeCliente ?? "", a.enderecoCliente ?? ""].some((e) =>
              e.toLowerCase().includes(filters.search.toLowerCase())
            )
          : true;

        const temaMatch = filters.tema ? a.localId === filters.tema : true;

        const vendedorMatch =
          typeof vendedorSelecionado === "number"
            ? a.usuarioId === vendedorSelecionado
            : true;

        let tipoClienteMatch = true;
        if (filters.tipoCliente === "PROSPECT") tipoClienteMatch = prospectSet.has(a.clienteId);
        else if (filters.tipoCliente === "RECEBEDOR") tipoClienteMatch = recebedorSet.has(a.clienteId);
        else if (filters.tipoCliente === "EMISSOR") tipoClienteMatch = emissorSet.has(a.clienteId);

        return searchMatch && temaMatch && vendedorMatch && tipoClienteMatch;
      });

    let resultados = [];

    if (filters.ordem === "1") {
      resultados = await query.sortBy("data");
      resultados = resultados.reverse();
    } else if (filters.ordem === "2") {
      resultados = await query.sortBy("data");
    } else {
      resultados = await query.toArray();
    }

    setVisitas(resultados.slice(0, skip + take));
  }, [skip, take, filters.search, filters.tema, filters.ordem, filters.tipoCliente, days, vendedorSelecionado]);

  const allVisitas = useLiveQuery(() => db.visitas.toArray(), []);

  const visitaMaisRecenteCliente = useMemo(() => {
    if (!Array.isArray(allVisitas)) return null;

    const visitasFiltradas = allVisitas
      .filter((visita) => visita.clienteId === +idCliente)
      .sort((a, b) => new Date(b.data) - new Date(a.data));

    return visitasFiltradas[0] || null;
  }, [allVisitas, idCliente]);

  useEffect(() => {
    if (!allVisitas) return;

    const locaisMap = allVisitas.reduce((acc, visita) => {
      const { local, localId } = visita;
      if (!acc[localId]) {
        acc[localId] = { local, localId };
      }
      return acc;
    }, {});
    setUniqueLocais(Object.values(locaisMap));
  }, [allVisitas]);

  const total = useLiveQuery(async () => {
    let recebedorSet = new Set();
    let emissorSet = new Set();
    let prospectSet = new Set();
    if (filters.tipoCliente) {
      const recebedorIds = await db.cliente.filter(c => (c.tipo === 3 || c.tipo === 4) && c.clienteProspect === 0).primaryKeys();
      recebedorSet = new Set(recebedorIds);
      const emissorIds = await db.cliente.filter(c => (c.tipo === 1 || c.tipo === 2) && c.clienteProspect === 0).primaryKeys();
      emissorSet = new Set(emissorIds);
      const prospectIds = await db.cliente.filter(c => c.clienteProspect === 1).primaryKeys();
      prospectSet = new Set(prospectIds);
    }

    return db.visitas
      .where("dataLabel")
      .anyOf(days)
      .and((a) => {
        const searchMatch = filters.search
          ? [
              (a.codigoCliente ?? "").toLowerCase(),
              (a.nomeCliente ?? "").toLowerCase(),
              (a.enderecoCliente ?? "").toLowerCase(),
            ].some((e) => e.includes(filters.search.toLowerCase()))
          : true;

        const temaMatch = filters.tema ? a.localId === filters.tema : true;

        const vendedorMatch =
          typeof vendedorSelecionado === "number"
            ? a.usuarioId === vendedorSelecionado
            : true;

        let tipoClienteMatch = true;
        if (filters.tipoCliente === "PROSPECT") tipoClienteMatch = prospectSet.has(a.clienteId);
        else if (filters.tipoCliente === "RECEBEDOR") tipoClienteMatch = recebedorSet.has(a.clienteId);
        else if (filters.tipoCliente === "EMISSOR") tipoClienteMatch = emissorSet.has(a.clienteId);

        return searchMatch && temaMatch && vendedorMatch && tipoClienteMatch;
      })
      .count();
  }, [filters.search, filters.tema, filters.tipoCliente, days, vendedorSelecionado]);

  useEffect(() => {
    if (filters.periodo) {
      if (filters.periodo === "hoje") {
        setDays([new Date().toLocaleDateString("pt-BR")]);
      } else if (filters.periodo === "essa-semana") {
        setDays(semanaVigente());
      } else if (filters.periodo === "esse-mes") {
        setDays(todasAsDatasDoMes());
      } else {
        setDays(filters.periodo);
      }
    }
  }, [filters.periodo]);


  const totalRenderizado = useMemo(() => (visitas  || []).length, [visitas ]);
  return {
    visitas,
    totalRenderizado,
    total,
    uniqueLocais,
    visitaMaisRecenteCliente,
  };
};


export default useVisitas;
